import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { money, PAYMENT_META } from "@/lib/pricing";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Send, Undo2, PlusCircle } from "lucide-react";
import { toast } from "sonner";

const Line = ({ l, v, bold, id }) => (
  <div className={`flex justify-between text-sm ${bold ? "font-bold text-sugar-ink" : "text-sugar-ink2"}`}><span>{l}</span><span data-testid={id}>{v}</span></div>
);

const ExtraCharge = ({ order, onDone }) => {
  const [desc, setDesc] = useState("");
  const [amt, setAmt] = useState("");
  const [busy, setBusy] = useState(false);
  const n = Number(amt) || 0;
  const unpaid = order.payment_status === "pending";
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/extra-charge`, { description: desc, amount: n, send_link: true });
      onDone(data.order); setDesc(""); setAmt("");
      toast.success(data.link ? `Added. Pay link for ${money(data.link.amount)} ${data.link.emailed ? "emailed to customer" : "created"}` : "Added to order");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="rounded-xl bg-sugar-cream p-4">
      <h4 className="flex items-center gap-1.5 text-sm font-bold text-sugar-ink"><PlusCircle size={15} className="text-sugar-pink" /> Add a charge</h4>
      <p className="mt-0.5 text-xs text-sugar-ink2">For something the customer forgot to pay for. 12% tax is added{unpaid ? "." : " and they get an email with a Stripe pay link."}</p>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <Input data-testid="admin-charge-desc" placeholder="What's it for? e.g. Extra butterflies" value={desc} onChange={(e) => setDesc(e.target.value)} className="flex-1 rounded-xl border-sugar-border bg-white" />
        <Input data-testid="admin-charge-amount" type="number" min="0" step="0.01" placeholder="Amount" value={amt} onChange={(e) => setAmt(e.target.value)} className="rounded-xl border-sugar-border bg-white sm:w-28" />
      </div>
      {n > 0 && <p data-testid="admin-charge-preview" className="mt-2 text-xs text-sugar-ink2">{money(n)} + {money(n * 0.12)} tax = <b>{money(n * 1.12)}</b></p>}
      <Button data-testid="admin-charge-submit" disabled={busy || !desc || n <= 0} onClick={submit} className="mt-3 rounded-full bg-sugar-ink font-bold text-white hover:bg-sugar-ink/90">
        {busy ? "Adding…" : unpaid ? "Add to order" : "Add & send pay link"}
      </Button>
    </div>
  );
};

const r2 = (n) => Math.round(n * 100) / 100;

const refundParts = (order, catalog) => {
  const parts = [];
  order.items.forEach((it, i) => {
    const style = (catalog?.styles || []).find((s) => s.id === it.style_id);
    const addons = [];
    for (const g of style?.groups || []) {
      if (!g.multi) continue;
      const val = it.selections?.[g.id];
      (Array.isArray(val) ? val : []).forEach((v) => {
        const o = (g.options || []).find((x) => x.value === v);
        if (o && o.price > 0) addons.push({ key: `item${i}:addon:${g.id}:${v}`, label: `${it.style_name} add-on: ${o.label}`, base: o.price });
      });
    }
    const addonSum = addons.reduce((s, a) => s + a.base, 0);
    parts.push({ key: `item${i}`, label: addons.length ? `${it.style_name} (cake, without add-ons)` : it.style_name, amount: r2((it.unit_price - addonSum) * 1.12) });
    addons.forEach((a) => parts.push({ key: a.key, label: a.label, amount: r2(a.base * 1.12) }));
  });
  if (order.delivery_fee > 0) parts.push({ key: "delivery", label: "Delivery fee", amount: r2(order.delivery_fee * 1.12) });
  if (order.rush_fee > 0) parts.push({ key: "rush", label: "Rush fee", amount: r2(order.rush_fee * 1.12) });
  if (order.tip > 0) parts.push({ key: "tip", label: "Tip", amount: r2(order.tip), noTax: true });
  return parts.filter((p) => p.amount > 0);
};

const Refund = ({ order, catalog, onDone }) => {
  const [picked, setPicked] = useState([]);
  const [custom, setCustom] = useState("");
  const [overpaid, setOverpaid] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const parts = refundParts(order, catalog);
  const done = new Set((order.refunds || []).flatMap((r) => (r.parts || []).map((p) => p.key)));
  const chosen = parts.filter((p) => picked.includes(p.key));
  const c = Math.max(0, Number(custom) || 0);
  const n = r2(chosen.reduce((s, p) => s + p.amount, 0) + c);
  const toggle = (k) => setPicked((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const submit = async () => {
    setBusy(true);
    const payload = [...chosen.map(({ key, label, amount }) => ({ key, label, amount })), ...(c > 0 ? [{ key: `custom-${Date.now()}`, label: "Custom amount", amount: r2(c) }] : [])];
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/refund`, { amount: n, reason, parts: payload, kind: overpaid && !chosen.length ? "overpaid" : "reduction" });
      onDone(data); setPicked([]); setCustom(""); setReason(""); setOverpaid(false); toast.success(`Refunded ${money(n)} to the customer's card`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false); setOpen(false);
  };
  return (
    <div className="rounded-xl bg-sugar-cream p-4">
      <h4 className="flex items-center gap-1.5 text-sm font-bold text-sugar-ink"><Undo2 size={15} className="text-sugar-pink" /> Refund</h4>
      <p className="mt-0.5 text-xs text-sugar-ink2">Tick what to refund (12% tax included, tips have no tax), or add a custom amount. Up to {money(order.net_paid)}.</p>
      <div className="mt-3 space-y-1.5">
        {parts.map((p) => (
          <label key={p.key} data-testid={`admin-refund-part-${p.key}`} className={`flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-2 text-sm ${done.has(p.key) ? "opacity-50" : "cursor-pointer"}`}>
            <span className="flex items-center gap-2">
              <input type="checkbox" disabled={done.has(p.key)} checked={picked.includes(p.key)} onChange={() => toggle(p.key)} className="h-4 w-4 accent-[#D98AA6]" />
              {p.label}{done.has(p.key) && <span className="text-[10px] font-bold uppercase text-red-500">refunded</span>}
            </span>
            <span className="font-semibold text-sugar-ink">{money(p.amount)}</span>
          </label>
        ))}
      </div>
      <div className="mt-2 flex flex-col gap-2 sm:flex-row">
        <Input data-testid="admin-refund-amount" type="number" min="0" step="0.01" placeholder="Custom $" value={custom} onChange={(e) => { setCustom(e.target.value); setOverpaid(false); }} className="rounded-xl border-sugar-border bg-white sm:w-28" />
        <Input data-testid="admin-refund-reason" placeholder="Reason (customer sees this)" value={reason} onChange={(e) => setReason(e.target.value)} className="flex-1 rounded-xl border-sugar-border bg-white" />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button data-testid="admin-refund-submit" disabled={busy || n <= 0 || n > order.net_paid + 0.001} onClick={() => setOpen(true)} className="rounded-full bg-red-500 font-bold text-white hover:bg-red-600">Refund {n > 0 ? money(n) : ""}</Button>
        {n > order.net_paid + 0.001 && <span className="text-xs font-bold text-red-600">More than what's left to refund</span>}
        {order.balance_due < 0 && <button data-testid="admin-refund-overpaid" onClick={() => { setPicked([]); setCustom((-order.balance_due).toFixed(2)); setOverpaid(true); }} className="text-xs font-bold text-sugar-pink hover:underline">Fill overpaid {money(-order.balance_due)}</button>}
        <button data-testid="admin-refund-full" onClick={() => { setPicked([]); setCustom(order.net_paid.toFixed(2)); setOverpaid(false); }} className="text-xs font-bold text-sugar-pink hover:underline">Full refund</button>
      </div>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Refund {money(n)}?</AlertDialogTitle>
            <AlertDialogDescription>This sends {money(n)} back to {order.customer_name}'s card and can't be undone. They'll get an email.{chosen.length ? ` Refunding: ${chosen.map((p) => p.label).join(", ")}${c > 0 ? " + custom amount" : ""}.` : ""}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-refund-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-refund-confirm" onClick={submit} className="bg-red-500 hover:bg-red-600">Yes, refund</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const OrderPaymentsPanel = ({ order, catalog, onSaved }) => {
  const [sending, setSending] = useState(false);
  const pay = PAYMENT_META[order.payment_status] || PAYMENT_META.pending;
  const sendLink = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/send-balance-link`);
      onSaved(data.order); toast.success(`Pay link for ${money(data.link.amount)} ${data.link.emailed ? "emailed to customer" : "created"}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSending(false);
  };
  return (
    <div className="space-y-4 rounded-2xl border border-sugar-border bg-white p-5">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-bold text-sugar-ink">Payment</h3>
        <span data-testid="admin-payment-status" className="rounded-full px-2.5 py-1 text-[11px] font-bold text-white" style={{ background: pay.color }}>{pay.label}</span>
      </div>
      <div className="space-y-1.5">
        <Line l="Subtotal" v={money(order.subtotal)} />
        {order.delivery_fee > 0 && <Line l="Delivery" v={money(order.delivery_fee)} />}
        {order.rush_fee > 0 && <Line l="Rush fee" v={money(order.rush_fee)} />}
        <Line l="GST + PST (12%)" v={money(order.tax)} />
        {order.tip > 0 && <Line l={`Tip${order.tip_percent ? ` (${order.tip_percent}%)` : ""}`} v={money(order.tip)} id="admin-order-tip" />}
        <Line l="Order total" v={money(order.total)} bold id="admin-order-total" />
        <Line l="Paid (after refunds)" v={money(order.net_paid)} id="admin-net-paid" />
        {order.amount_refunded > 0 && <Line l="Refunded" v={`-${money(order.amount_refunded)}`} id="admin-refunded" />}
        {order.balance_due !== 0 && order.payment_status !== "pending" && (
          <div data-testid="admin-balance" className={`mt-2 rounded-xl px-3 py-2 text-sm font-bold ${order.balance_due > 0 ? "bg-yellow-200 text-sugar-ink" : "bg-indigo-50 text-indigo-700"}`}>
            {order.balance_due > 0 ? `Customer owes ${money(order.balance_due)}` : `Customer overpaid ${money(-order.balance_due)} — refund below`}
          </div>
        )}
      </div>
      {order.balance_due > 0 && order.payment_status !== "pending" && (
        <Button data-testid="admin-send-balance-link" disabled={sending} onClick={sendLink} className="w-full rounded-full bg-sugar-pink font-bold text-white hover:bg-sugar-pinkhover"><Send size={15} className="mr-1.5" />{sending ? "Sending…" : `Email pay link for ${money(order.balance_due)}`}</Button>
      )}
      <ExtraCharge order={order} onDone={onSaved} />
      {order.net_paid > 0 && <Refund order={order} catalog={catalog} onDone={onSaved} />}
      {(order.refunds?.length > 0 || order.charges?.length > 0) && (
        <div>
          <h4 className="text-xs font-bold uppercase tracking-widest text-sugar-pink">History</h4>
          <ul className="mt-2 space-y-1 text-xs text-sugar-ink2">
            {(order.charges || []).map((c, i) => <li key={`c${i}`} data-testid={`admin-charge-${i}`}>Pay link {money(c.amount)} · {c.status === "paid" ? "PAID" : "sent"} · {new Date(c.at).toLocaleDateString()}</li>)}
            {(order.refunds || []).map((r, i) => <li key={`r${i}`} data-testid={`admin-refund-${i}`}>Refund {money(r.amount)}{r.parts?.length ? ` (${r.parts.map((p) => p.label).join(", ")})` : ""}{r.reason ? ` — ${r.reason}` : ""} · {new Date(r.at).toLocaleDateString()}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
};
